import { useCallback, useState } from 'react';
import { showErrorAlert, showSuccessAlert } from 'utils/AlertUtils';
import * as labelApi from 'utils/labelApi';

// Manages the label collection for the Labels list page (RAT-1688/1699):
// local state + async actions with alert feedback, no Redux (matches the
// small, admin-curated scale of this resource - no dedicated slice/thunk
// needed). Create/Edit/Delete are their own pages/flows (LabelAddEdit,
// useLabelDetail) - this hook only covers what the list itself does:
// fetching and Change scope.
const useLabels = () => {
  const [labels, setLabels] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(10);
  const [isFetching, setIsFetching] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const errorMessage = (error, fallback) =>
    error?.message || error?.error?.message || fallback;

  const fetchLabels = useCallback(async (filters = {}) => {
    setIsFetching(true);
    try {
      const data = await labelApi.listLabels(filters);
      setLabels(data?.list || []);
      setTotal(data?.total || 0);
      setLimit(data?.limit || 10);
    } catch (error) {
      showErrorAlert(errorMessage(error, 'Failed to load labels'));
    } finally {
      setIsFetching(false);
    }
  }, []);

  const changeScope = useCallback(
    async (id, payload, filters) => {
      setIsSaving(true);
      try {
        await labelApi.changeLabelScope(id, payload);
        await fetchLabels(filters);
        showSuccessAlert('Label scope updated');
        return true;
      } catch (error) {
        showErrorAlert(errorMessage(error, 'Failed to change scope'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [fetchLabels],
  );

  return {
    labels,
    total,
    limit,
    isFetching,
    isSaving,
    fetchLabels,
    changeScope,
  };
};

export default useLabels;
